// Edity — wspólne kawałki listy, strony serii i strony editu (sesja 09).
//
//   EditThumb        kafel editu: PRAWDZIWY kadr (`thumb_url`, `Media` 4:3 wykadrowane ze
//                    środka 9:16 — decyzja 06 nr 4) albo STAN (renderuje N z M · w kolejce ·
//                    nie wyszedł · generuje miniaturę). Nigdy gradient. Jedna definicja dla
//                    paska karty serii, siatki jobów i widoku płaskiego (G5-edits-30).
//   PreviewEdit      nakładka podglądu w języku minimal (sesja 30, artboard 07): JEDEN
//                    odtwarzacz (`ds/media/Player`) i kolumna akcji na jednej ciemnej
//                    warstwie, nazwa serii, „edit a z b", pobranie i usunięcie
//                    jako jedyna akcja wypełniona i „Zamknij" jako cicha droga wyjścia.
//                    Strzałki ←/→ chodzą po gotowych editach bez własnej etykiety na
//                    ekranie. Na 390 okno jest CAŁYM ekranem. Fakty o pliku (styl, paczki,
//                    długość, rozmiar) nie zajmują podglądu — okno ma pokazać
//                    film, nie metrykę.
//   DownloadEdit     pobranie jednego MP4 przez krótkotrwały podpisany adres.
//   ArchiveDownload  pobieranie ZIP jako STAN: przygotowanie, strumień, postęp per plik,
//                    „Przerwij" (keep). Wyłączony NIE JEST niemy: powód blokady idzie do
//                    czytnika (`aria-describedby` na tekst `sr-only`) i do `title`, a na
//                    ekranie zostaje sam nieaktywny przycisk — bez zdania (paczka N2, Z1).
//   RefundFact       fakt z ledgera przy pigułce nieudanego editu (paczka N2, Z0).
//   PublicationPill  pigułka publikacji gotowego editu (reguła 59): „opublikowany” albo
//                    „zaplanowany”; ten sam slot co pigułka stanu renderu.
//   TipWrap          podpowiedź powodu blokady przy ikonie (kosz).

import type { KeyboardEvent, ReactNode } from 'react'
import { Fragment, useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router'
import Icon from './Icon'
import { Button } from '../ds/primitives/Button'
import { Progress } from '../ds/primitives/Progress'
import { StatusPill } from '../ds/primitives/StatusPill'
import { Media } from '../ds/media/Media'
import { Player, clock } from '../ds/media/Player'
import type { PlayerLabels } from '../ds/media/Player'
import { Dialog } from '../ds/feedback/Dialog'
import { Notice } from '../ds/feedback/Notice'
import { Skeleton } from '../ds/states/StatePanel'
import { useGeneratedAt } from '../hooks/useGeneratedAt'
import { useI18n, useT } from '../lib/i18n'
import { formatNumber } from '../locales/format'
import { pluralForm } from '../locales/plural'
import { TokenAmount } from '../ds/primitives/TokenAmount'
import { deleteRenderJob, fetchRenderJob } from '../lib/edits'
import { saveEdit } from '../lib/saveEdit'
import { notifySeriesChanged } from '../lib/seriesEvents'
import { archivePath, fetchArchivePlan } from '../lib/series'
import type { ArchivePlan, SeriesJob } from '../lib/series'
import type { EditPublication } from '../lib/types'
import { trackDisplayName } from '../lib/trackName'
import '../routes/series.css'

export function useSizeLabel() {
  const { locale } = useI18n()
  const t = useT()
  return (bytes: number | null | undefined) => {
    if (bytes == null) return t('series.unknownSize')
    const mb = bytes / 1024 / 1024
    if (mb >= 1024) return `${formatNumber(locale, mb / 1024, { maximumFractionDigits: 1 })} GB`
    return `${formatNumber(locale, mb, { maximumFractionDigits: mb >= 10 ? 0 : 1 })} MB`
  }
}

/** „3 gemy" / „1 gem" / „12 gemów" — jedna funkcja dla ceny, kosztu i zwrotu. */
export function useGemsLabel() {
  const { locale } = useI18n(); const t = useT()
  return (n: number) => t(`series.gemsWord_${pluralForm(locale, n)}`, { n })
}

/** Etykiety odtwarzacza z locale — DS ich nie tłumaczy. */
export function usePlayerLabels(): PlayerLabels {
  const t = useT()
  return {
    play: t('series.play'), pause: t('series.pause'), seek: t('series.seek'), volume: t('series.volume'),
    mute: t('series.mute'), unmute: t('series.unmute'), fullscreen: t('series.fullscreen'), playing: t('series.playing'),
    loading: t('common.loading'),
  }
}

/** 390 = arkusz od dołu (decyzja 1); szerzej = dialog. Ten sam próg co arkusz w ds.css. */
export function useNarrow(maxWidth = 560): boolean {
  const query = `(max-width: ${maxWidth}px)`
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches)
  useEffect(() => {
    const mq = window.matchMedia?.(query)
    if (!mq) return
    const on = () => setNarrow(mq.matches)
    on()
    mq.addEventListener?.('change', on)
    return () => mq.removeEventListener?.('change', on)
  }, [query])
  return narrow
}

/** Skrót identyfikatora do chipa / numeru serii — nigdy pełny UUID jako tytuł. */
export const shortId = (id: string) => id.replace(/-/g, '').slice(0, 8)

export function EditThumb({ job, onPreview, small = false, showDuration = false }: {
  job: SeriesJob; onPreview?: () => void; small?: boolean; showDuration?: boolean
}) {
  const t = useT()
  const [badImage, setBadImage] = useState(false)
  useEffect(() => setBadImage(false), [job.thumb_url])
  const status = (['queued', 'rendering', 'done', 'failed'] as const).includes(job.status as never) ? job.status : 'queued'
  const playable = Boolean(job.downloadable && onPreview)
  const duration = showDuration && job.status === 'done' && job.duration_sec != null ? clock(job.duration_sec) : undefined

  let media: ReactNode
  if (job.thumb_url && !badImage) {
    media = (
      <Media ratio="4:3" src={job.thumb_url} alt="" duration={duration} onError={() => setBadImage(true)}>
        {playable ? <span className="ethumb-play" aria-hidden="true"><Icon name="Play" size={small ? 14 : 18} strokeWidth={2} /></span> : null}
      </Media>
    )
  } else {
    const state = status === 'rendering'
      ? { title: t('appBatches.job.rendering'),
          text: job.completed_chunks != null && job.total_chunks != null
            ? (small ? t('series.chunks', { done: job.completed_chunks, total: job.total_chunks })
              : `${job.progress} % · ${t('series.chunks', { done: job.completed_chunks, total: job.total_chunks })}`)
            : `${job.progress} %`,
          progress: (job.progress ?? 0) / 100 }
      : status === 'queued'
        ? { title: t('appBatches.job.queued') }
        : status === 'failed'
          ? { title: t('appBatches.job.failed'), glyph: small ? undefined : 'edit' as const }
          : { title: t(job.thumbnail_state === 'pending' ? 'series.thumbPending' : 'series.thumbUnavailable') }
    media = (
      <Media ratio="4:3" state={state}>
        {playable ? <span className="ethumb-play" aria-hidden="true"><Icon name="Play" size={small ? 14 : 18} strokeWidth={2} /></span> : null}
        {status === 'rendering' && typeof job.progress === 'number' ? <span hidden data-testid="job-pct" data-progress={job.progress} /> : null}
      </Media>
    )
  }
  const cls = ['ethumb', small && 'is-small', `is-${status}`].filter(Boolean).join(' ')
  return playable
    ? <button type="button" className={cls} onClick={onPreview} aria-label={t('series.preview', { n: job.position + 1 })} data-testid={`preview-${job.id}`}>{media}</button>
    : <div className={cls} data-status={status}>{media}</div>
}

/**
 * Reguła 59: gotowy edit, który jest w planie w toku albo już wyszedł, nosi JEDNĄ pigułkę
 * ze słowem — „opublikowany” (`muted`, fakt spokojny i nieodwracalny) albo „zaplanowany”
 * (`info`, rzecz w toku). Stan wybiera serwer (`publication.state`: cięższy wygrywa),
 * ekran go nie przelicza. Pigułka jest tekstem, więc czytnik słyszy słowo.
 */
export function PublicationPill({ publication, className }: { publication?: EditPublication | null; className?: string }) {
  const t = useT()
  if (!publication) return null
  return <StatusPill tone={publication.state === 'published' ? 'muted' : 'info'} className={className}
    data-testid="publication-pill" data-status={publication.state}>{t(`appBatches.publication.${publication.state}`)}</StatusPill>
}

/**
 * Ta sama rzecz jako DANA dla górnej linii okna podglądu: słowo stanu · liczba kont ·
 * termin („opublikowany · 2 konta · 21 wrz, 18:00”). Termin to ostatnia publikacja albo
 * najbliższy post — tym samym formatem co data wygenerowania (reguła 31).
 */
export function usePublicationFact() {
  const t = useT(); const { locale } = useI18n(); const when = useGeneratedAt()
  return (publication?: EditPublication | null): string | null => {
    if (!publication) return null
    const term = when(publication.state === 'published' ? publication.last_published_at : publication.next_scheduled_at)
    return [
      t(`appBatches.publication.${publication.state}`),
      t(`appBatches.publication.accounts_${pluralForm(locale, publication.accounts)}`, { n: publication.accounts }),
      term,
    ].filter(Boolean).join(' · ')
  }
}

/** Dawna nazwa kafla (GeneratorSeriesCard, paczka 08) — ta sama definicja. */
export const SeriesThumb = EditThumb

/** Powód blokady stoi PRZY ikonie: podpowiedź na hover / fokus, czytana przez aria-describedby. */
export function TipWrap({ reason, children }: { reason?: string; children: ReactNode }) {
  const id = useId()
  if (!reason) return <>{children}</>
  return (
    <span className="tip-wrap" tabIndex={0} aria-describedby={id}>
      {children}
      <span className="tip" id={id} role="tooltip">{reason}</span>
    </span>
  )
}

type PreviewProps = { jobs: SeriesJob[]; index: number; setIndex: (n: number) => void; onClose: () => void; onDeleted?: () => void; seriesName?: string | null; seriesId?: string | null }
export function PreviewEdit(props: PreviewProps) {
  return <PreviewEditContent key={props.jobs[props.index].id} {...props} />
}

function PreviewEditContent({ jobs, index, setIndex, onClose, onDeleted, seriesName }: PreviewProps) {
  const t = useT()
  const job = jobs[index]
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const labels = usePlayerLabels()
  const titleId = useId()
  const when = useGeneratedAt()
  const publication = usePublicationFact()
  // Reguła 59: po dacie wygenerowania ta sama linia niesie fakt publikacji jako daną.
  const date = [when(job.finished_at ?? job.created_at), publication(job.publication)].filter(Boolean).join(' · ')
  useEffect(() => {
    let alive = true
    setUrl(null); setError(false)
    fetchRenderJob(job.id).then(r => { if (alive) { setUrl(r.preview_url); setError(!r.preview_url) } })
      .catch(() => { if (alive) setError(true) })
    return () => { alive = false }
  }, [job.id])
  useEffect(() => {
    const onKey = (e: KeyboardEvent | globalThis.KeyboardEvent) => {
      if (deleting) return
      const target = e.target as HTMLElement | null
      if (target?.getAttribute?.('role') === 'slider') return
      if (e.key === 'ArrowLeft' && index > 0) { e.preventDefault(); setIndex(index - 1) }
      if (e.key === 'ArrowRight' && index < jobs.length - 1) { e.preventDefault(); setIndex(index + 1) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [index, jobs.length, setIndex, deleting])
  const name = seriesName || job.batch_title || trackDisplayName(job.music_name) || t('appBatches.card.untitled')
  // A file that will not play keeps its real frame (poster) and says so under the transport —
  // never a blank state over a frame we do have. Without a URL at all there is nothing to draw.
  const stage = !url
    ? (error ? <Media ratio="9:16" state={{ title: t('series.actionError'), glyph: 'edit' }} /> : <Skeleton variant="thumb" />)
    : <>
      <Player src={url} poster={job.thumb_url} labels={labels} durationHint={job.duration_sec} onError={() => setError(true)} />
      {error && <Notice tone="danger" className="preview-error">{t('series.actionError')}</Notice>}
    </>
  const close = <button type="button" className="mn-lnk mn-pv-close" onClick={onClose} aria-label={t('common.close')} data-testid="preview-close">
    <Icon name="X" size={18} />
  </button>
  return <>
    {/* One existing preview on both desktop and phone; no separate edit page. */}
    {/* R4: w oknie stoi WYŁĄCZNIE wideo 9:16, jeden rząd akcji i zamknięcie.
        Bez nazwy serii, numeru editu, czasu, rozmiaru i identyfikatora — te rzeczy
        wróciły na stronę serii. Rząd akcji leży na tej samej ciemnej warstwie co
        wideo, bez białej karty i bez ramki (R4b: przy 86 % kafle prześwitywały). */}
    <Dialog onClose={onClose} labelledBy={titleId} wide className="mn-pv">
      <div className="mn-pv-box">
        {/* Reguła 31: górna linia okna niesie datę wygenerowania. Edit bez pliku
            (w kolejce) nie ma daty i wtedy linia po prostu nie powstaje. Reguła 59:
            edit w planie albo po publikacji dokłada do niej słowo stanu, konta i termin. */}
        {/* Na 360 linia się łamie — ale MIĘDZY faktami, nigdy w środku terminu (PN #19). */}
        {date && <div className="mn-pv-date mn-dtm" data-testid="preview-date">
          {date.split(' · ').map((fact, i) => <Fragment key={i}>{i > 0 && ' · '}<span className="mn-pv-fact">{fact}</span></Fragment>)}
        </div>}
        <div className="mn-pv-stage">{stage}</div>
        <div className="mn-pv-title sr-only">
          <b id={titleId}>{name}</b>
          <span className="mn-pv-pos" data-testid="preview-position">{t('series.position', { n: index + 1, total: jobs.length })}</span>
        </div>
        {/* Reguła 28.3: „Usuń” to mały przycisk przy prawej krawędzi wideo, nie link;
            „Pobierz MP4” bierze szerokość ze swojej etykiety, bez `min-width`. */}
        <div className="mn-pv-actions">
          <DownloadEdit id={job.id} variant="primary" label={t('dialogs.done.download')} />
          <button type="button" className="mn-sbt mn-sbt--dark mn-pv-del" onClick={() => setDeleting(true)} data-testid="preview-delete">{t('appEditDetail.actions.delete')}</button>
        </div>
      </div>
      {close}
    </Dialog>
    {deleting && <DeleteEditDialog job={job} name={name} onClose={() => setDeleting(false)} onDeleted={() => { onDeleted?.(); onClose() }} />}
  </>
}

export function DeleteEditDialog({ job, name, onClose, onDeleted }: {
  job: SeriesJob; name: string; onClose: () => void; onDeleted: () => void
}) {
  const t = useT()
  const size = useSizeLabel()
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState(false)
  const remove = async () => {
    if (deleteBusy) return
    setDeleteBusy(true); setDeleteError(false)
    try {
      await deleteRenderJob(job.id)
      notifySeriesChanged()
      onDeleted()
    } catch { setDeleteError(true); setDeleteBusy(false) }
  }
  return <Dialog onClose={deleteBusy ? undefined : onClose} className="mn-dlg" title={t('appEditDetail.deleteDialog.title')}
      footer={(
        <>
        <Button variant="quiet" data-autofocus disabled={deleteBusy} onClick={onClose}>{t('appEditDetail.deleteDialog.cancel')}</Button>
        <Button variant="primary" className="mn-btn-danger" busy={deleteBusy} onClick={() => void remove()}>{t('appEditDetail.deleteDialog.confirm')}</Button>
        </>
      )}
      footerClassName="dg-foot--pair"
    >
      <div className="mn-nums"><div className="mn-nums-row"><span>{t('appEditDetail.title', { series: name, n: job.position + 1 })}</span><b>{size(job.size_bytes)}</b></div></div>
      {deleteError && <Notice tone="danger">{t('appEditDetail.actions.deleteFailed')}</Notice>}
    </Dialog>
}

export function DownloadEdit({ id, variant = 'quiet', size = 'sm', block, label }: {
  id: string; variant?: 'primary' | 'secondary' | 'quiet'; size?: 'md' | 'sm'; block?: boolean; label?: string
}) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  return <span className={block ? 'btn-wrap btn-wrap-block' : 'btn-wrap'}>
    <Button variant={variant} size={size} block={block} busy={busy} data-testid={`job-download-${id}`} onClick={async () => {
      setBusy(true); setError(false)
      try {
        await saveEdit(id)
      } catch { setError(true) } finally { setBusy(false) }
    }}><Icon name="Download" size={16} />{label ?? t('appBatches.job.download')}</Button>
    {error && <span className="btn-reason" role="alert">{t('series.actionError')}</span>}
  </span>
}

interface WritableFile { write(chunk: Uint8Array): Promise<void>; close(): Promise<void>; abort(): Promise<void> }
interface SaveHandle { createWritable(): Promise<WritableFile> }
type PickerWindow = Window & { showSaveFilePicker?: (opts: unknown) => Promise<SaveHandle> }

export function ArchiveDownload({ ids, count, sizeBytes, disabled, disabledReason, variant = 'secondary', size = 'md', label, block }: {
  ids: string[]; count: number; sizeBytes?: number | null; disabled?: boolean
  /** DLACZEGO archiwum nie powstanie. Nie jest zdaniem na ekranie: czyta je czytnik
   *  ekranu (`aria-describedby`) i pokazuje `title` — widocznym nośnikiem stanu
   *  zostaje sam nieaktywny przycisk (zasada 8 + reguła 19). */
  disabledReason?: string
  variant?: 'primary' | 'secondary' | 'quiet'; size?: 'md' | 'sm'; label?: string; block?: boolean
}) {
  const t = useT()
  const reasonId = useId()
  const [state, setState] = useState<'idle' | 'preparing' | 'downloading' | 'handed' | 'done' | 'error'>('idle')
  const [received, setReceived] = useState(0)
  const [plan, setPlan] = useState<ArchivePlan | null>(null)
  const ctrl = useRef<AbortController | null>(null)
  const sizeLabel = useSizeLabel()
  useEffect(() => () => ctrl.current?.abort(), [])
  const busy = state === 'preparing' || state === 'downloading'
  const start = async () => {
    setState('preparing'); setReceived(0); setPlan(null)
    const aborter = new AbortController(); ctrl.current = aborter
    let writer: WritableFile | undefined
    try {
      // Ask while the button activation is live. Streams go to disk, never a
      // multi-GB Blob. Browsers without this API use their native downloader.
      const handlePromise = (window as PickerWindow).showSaveFilePicker?.({ suggestedName: 'edimi-edity.zip', types: [{ description: 'ZIP', accept: { 'application/zip': ['.zip'] } }] })
      const handle = handlePromise ? await handlePromise : null
      const next = await fetchArchivePlan(ids)
      if (aborter.signal.aborted) return
      if (!next.archive.available) throw new Error('archive_unavailable')
      setPlan(next)
      if (!handle) {
        const a = document.createElement('a'); a.href = archivePath(ids); a.download = 'edimi-edity.zip'
        document.body.append(a); a.click(); a.remove(); setState('handed'); return
      }
      writer = await handle.createWritable()
      const response = await fetch(archivePath(ids), { credentials: 'include', signal: aborter.signal })
      if (!response.ok || !response.body || !response.headers.get('content-type')?.includes('application/zip')) throw new Error('archive_failed')
      const length = Number(response.headers.get('content-length'))
      // If the available members changed since the plan, show bytes alone.
      if (length !== next.size_bytes) setPlan({ archive: next.archive, size_bytes: length || undefined })
      setState('downloading')
      const reader = response.body.getReader()
      let n = 0
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        await writer.write(value); n += value.byteLength; setReceived(n)
      }
      if (length && n !== length) throw new Error('archive_incomplete')
      await writer.close(); writer = undefined; setState('done')
    } catch (e) {
      if (writer) await writer.abort().catch(() => {})
      setState(aborter.signal.aborted || (e instanceof DOMException && e.name === 'AbortError') ? 'idle' : 'error')
    }
  }
  // PL1 (24.09): nic do pobrania = brak przycisku, nie szary „Pobierz ZIP (0)”.
  if (count === 0 && !label) return null
  const text = label ?? (sizeBytes != null ? t('series.zipWithSize', { n: count, size: sizeLabel(sizeBytes) }) : t('series.zipShort', { n: count }))
  const unavailable = Boolean(disabled)
  const reason = unavailable ? disabledReason : undefined
  return <div className={block ? 'series-download btn-wrap btn-wrap-block' : 'series-download'}>
    <Button variant={variant} size={size} block={block} busy={busy} disabled={unavailable}
            title={reason} aria-describedby={reason ? reasonId : undefined}
            data-testid={!unavailable && count ? 'batch-zip' : undefined} onClick={() => void start()}>
      <Icon name="Download" size={16} />{text}
    </Button>
    {reason && <span className="sr-only" id={reasonId} data-testid="batch-zip-reason">{reason}</span>}
    {state !== 'idle' && <div className="series-download-state" role="status">
      <strong>{t(state === 'preparing' ? 'series.zipPreparing' : state === 'downloading' ? 'series.zipDownloading' : state === 'handed' ? 'series.zipHanded' : state === 'done' ? 'series.zipDone' : 'series.actionError')}</strong>
      {state === 'downloading' && <>
        <Progress value={plan?.size_bytes ? received / plan.size_bytes : 0} label={t('series.zipDownloading')} />
        <span>{sizeLabel(received)}{plan?.size_bytes ? ` / ${sizeLabel(plan.size_bytes)}` : ''}</span>
        <ul>{plan?.files?.map(f => <li key={f.name}>{f.name}: {sizeLabel(Math.max(0, Math.min(f.size_bytes, received - f.start)))} / {sizeLabel(f.size_bytes)}</li>)}</ul></>}
      {busy && <Button variant="quiet" size="sm" onClick={() => { ctrl.current?.abort(); setState('idle') }}>{t('series.zipCancel')}</Button>}
    </div>}
  </div>
}

/**
 * Rozliczenie nieudanego editu — FAKT Z LEDGERA, nie zdanie (paczka N2, Z0).
 *
 * Inwariant 5 z CLAUDE.md („token charge tylko za SUKCES") ma jedno miejsce, w którym
 * klient widzi go przy SWOJEJ serii: kafel editu, który nie wyszedł. Potwierdzony wiersz
 * ledgera (`refund_confirmed`) wraca jako liczba z jednostką w kroju mono i z nazwą dla
 * czytnika; dopóki wiersza nie ma, stoi pigułka stanu rozliczenia. Nigdy akapit
 * i nigdy porównanie sald — każde inne uznanie wyglądałoby tak samo.
 */
export function RefundFact({ job }: { job: SeriesJob }) {
  const t = useT(); const { locale } = useI18n()
  if (job.refund_confirmed !== true) {
    return <StatusPill tone="muted" className="sdet-refund-pending" data-testid="job-refund-pending">
      {t('series.refundPending')}
    </StatusPill>
  }
  const n = job.refund_gems ?? 0
  // Paczka P1 C: zwrot jest DANĄ — znak, moneta, liczba. Nazwa faktu i pełna kwota
  // ze słowem dalej docierają do czytnika ekranu (nazwa tutaj, kwota w `TokenAmount`).
  return <span className="sdet-refund" data-testid="job-refund" data-gems={n}>
    <span className="sr-only">{t('series.refundBack')}: </span>
    <TokenAmount locale={locale} n={n} place="table" sign="+" />
  </span>
}

/** Link do strony editu z tytułem „seria · edit N" — jeden wzór dla kafli. */
export function EditName({ job, seriesName }: { job: SeriesJob; seriesName?: string | null }) {
  const t = useT()
  const name = seriesName || job.batch_title || trackDisplayName(job.music_name) || t('appBatches.card.untitled')
  const parent = job.batch_id ? `/edits/${encodeURIComponent(job.batch_id)}?` : '/edits?mode=all&'
  return <Link className="ecard-link" to={`${parent}preview=${encodeURIComponent(job.id)}`} title={`${name} · ${t('appEditDetail.crumb.edit', { n: job.position + 1 })}`}>{name}</Link>
}
